import { AppState, type AppStateData } from '../../core/AppState';
import { HexUtils } from '../../core/HexUtils';

export class HexEditor {
  private container: HTMLElement;
  private state: AppState;
  private PAGE_SIZE = 8192;
  private nextEditIndex: number | null = null;
  private lastMarkerIndex: number = -1;

  constructor(container: HTMLElement, state: AppState) {
    this.container = container;
    this.state = state;

    this.render(this.state.current);
    this.state.subscribe((data) => this.render(data));
  }

  private render(data: AppStateData) {
    // Capture previous scroll position
    let previousScrollTop = 0;

    // Check if we changed marker. If so, don't restore scroll.
    const markerChanged = this.lastMarkerIndex !== data.selectedMarkerIndex;
    this.lastMarkerIndex = data.selectedMarkerIndex;

    const existingGrid = this.container.querySelector('.hex-grid');
    if (existingGrid && !markerChanged) {
      previousScrollTop = existingGrid.scrollTop;
    }

    this.container.innerHTML = '';
    this.container.style.display = 'flex';
    this.container.style.flexDirection = 'column';
    this.container.style.height = '100%';

    // Header with Pagination
    const header = document.createElement('div');
    header.className = 'pane-header';

    const m = data.markers[data.selectedMarkerIndex];
    let title = 'HEX EDITOR';
    if (m) {
      title += ` @ 0x${m.start.toString(16).toUpperCase()} (${m.name})`;
    }

    const titleSpan = document.createElement('span');
    titleSpan.textContent = title;
    header.appendChild(titleSpan);

    if (m && m.size > this.PAGE_SIZE) {
      const totalPages = Math.ceil(m.size / this.PAGE_SIZE);
      const controls = document.createElement('div');
      controls.className = 'pagination';
      controls.style.display = 'flex'; controls.style.gap = '5px';

      const prev = document.createElement('button');
      prev.textContent = '◀';
      prev.disabled = data.editPage === 0;
      prev.onclick = () => this.state.setPage(data.editPage - 1);

      const label = document.createElement('span');
      label.textContent = `${data.editPage + 1} / ${totalPages}`;

      const next = document.createElement('button');
      next.textContent = '▶';
      next.disabled = data.editPage >= totalPages - 1;
      next.onclick = () => this.state.setPage(data.editPage + 1);

      controls.append(prev, label, next);
      header.appendChild(controls);
    }
    this.container.appendChild(header);

    // Toolbar (Replace)
    if (m) {
      const toolbar = document.createElement('div');
      toolbar.style.padding = '0.4rem 1rem';
      toolbar.style.background = '#1a1a1a';
      toolbar.style.borderBottom = '1px solid var(--border)';
      toolbar.style.display = 'flex';
      toolbar.style.gap = '10px';
      toolbar.style.alignItems = 'center';

      toolbar.innerHTML = `
            <span style="color:#888; font-size:0.75rem">REPLACE:</span>
            <input type="text" id="findInput" class="hex-input" placeholder="FF" maxlength="2" style="background:#333; border:1px solid #444; color:#fff; width:3em; text-align:center;">
            <span style="color:#666">→</span>
            <input type="text" id="replaceInput" class="hex-input" placeholder="00" maxlength="2" style="background:#333; border:1px solid #444; color:#fff; width:3em; text-align:center;">
            <button id="replaceBtn" style="padding:2px 8px; font-size:0.7rem;">Run</button>
        `;

      // Find inputs and attach listeners
      // Note: In a real app we might want to preserve input values across renders
      // OR make this toolbar part of the static layout and only update listeners.
      // For now, re-rendering is safe but clears basic input state.
      // Let's attach listener via delegation or direct lookup after append.

      this.container.appendChild(toolbar);

      const findInput = toolbar.querySelector('#findInput') as HTMLInputElement;
      const replaceInput = toolbar.querySelector('#replaceInput') as HTMLInputElement;
      const replaceBtn = toolbar.querySelector('#replaceBtn') as HTMLButtonElement;

      const sanitize = (e: Event) => {
        const input = e.target as HTMLInputElement;
        input.value = input.value.toUpperCase().replace(/[^0-9A-F]/g, '');
      };
      findInput.oninput = sanitize;
      replaceInput.oninput = sanitize;

      replaceBtn.onclick = () => {
        const f = parseInt(findInput.value, 16);
        const r = parseInt(replaceInput.value, 16);
        if (!isNaN(f) && !isNaN(r) && data.buffer) {
          const newBuf = HexUtils.replaceRange(data.buffer, m.start, m.end, f, r);
          if (newBuf) {
            this.state.updateBuffer(newBuf);
          } else {
            alert('Value not found in this segment.');
          }
        }
      };
    }

    // Grid
    const grid = document.createElement('div');
    grid.className = 'hex-grid';
    grid.style.flex = '1';
    grid.style.overflowY = 'auto';
    grid.style.padding = '1rem';
    grid.style.fontFamily = 'var(--font-mono)';
    grid.style.fontSize = '0.85rem';
    grid.style.lineHeight = '1.5';
    grid.style.color = '#ccc';

    if (!m) {
      grid.innerHTML = '<div style="color:#555; text-align:center; margin-top:20px;">Select a segment from the structure tree</div>';
      this.container.appendChild(grid);
      return;
    }

    const startOffset = m.start + (data.editPage * this.PAGE_SIZE);
    const endOffset = Math.min(m.end, startOffset + this.PAGE_SIZE, data.buffer!.length);

    const rowSize = 16;
    for (let i = startOffset; i < endOffset; i += rowSize) {
      const row = document.createElement('div');
      row.style.display = 'flex';

      const offsetEl = document.createElement('div');
      offsetEl.style.color = '#555';
      offsetEl.style.marginRight = '1.5rem';
      offsetEl.style.width = '60px';
      offsetEl.textContent = i.toString(16).toUpperCase().padStart(6, '0');
      row.appendChild(offsetEl);

      const bytesEl = document.createElement('div');
      bytesEl.style.display = 'flex';
      bytesEl.style.gap = '0.5rem';
      bytesEl.style.marginRight = '1.5rem';

      let asciiStr = '';

      for (let j = 0; j < rowSize; j++) {
        const globalIdx = i + j;
        if (globalIdx >= endOffset) {
          // padding
          const pad = document.createElement('span');
          pad.style.width = '1.4em';
          pad.style.visibility = 'hidden';
          bytesEl.appendChild(pad);
          asciiStr += ' ';
          continue;
        }

        const val = data.buffer![globalIdx];
        const hex = HexUtils.toHex(val);
        asciiStr += HexUtils.toAscii(val);

        const isChanged = data.originalBuffer && data.originalBuffer[globalIdx] !== val;

        const byteEl = document.createElement('span');
        byteEl.dataset.index = globalIdx.toString();
        byteEl.textContent = hex;
        byteEl.className = `hex-byte ${isChanged ? 'changed' : ''}`;
        byteEl.style.width = '1.4em';
        byteEl.style.textAlign = 'center';
        byteEl.style.cursor = 'pointer';
        byteEl.style.borderRadius = '2px';

        const isStructure = globalIdx < m.payloadStart;
        if (isStructure) {
          byteEl.classList.add('structure');
        }

        byteEl.onmouseenter = () => { byteEl.style.backgroundColor = '#333'; };
        byteEl.onmouseleave = () => {
          byteEl.style.backgroundColor = ''; // Clear hover
          // Classes handle the rest
        };

        byteEl.onclick = () => {
          this.enterEditMode(byteEl, globalIdx, hex);
        };

        bytesEl.appendChild(byteEl);
      }
      row.appendChild(bytesEl);

      const asciiEl = document.createElement('div');
      asciiEl.style.color = '#666';
      asciiEl.style.letterSpacing = '0.1em';
      asciiEl.style.whiteSpace = 'pre';
      asciiEl.textContent = asciiStr;
      row.appendChild(asciiEl);

      grid.appendChild(row);
    }

    this.container.appendChild(grid);

    // Restore scroll position
    if (previousScrollTop > 0) {
      grid.scrollTop = previousScrollTop;
    }

    // Handle pending edit focus
    if (this.nextEditIndex !== null) {
      // Small timeout to ensure DOM is ready? synchronous appendChild should be fine.
      const el = this.container.querySelector(`span[data-index="${this.nextEditIndex}"]`) as HTMLElement;
      if (el) {
        const idx = this.nextEditIndex;
        this.nextEditIndex = null;
        this.enterEditMode(el, idx, HexUtils.toHex(this.state.current.buffer![idx]));
      } else {
        // Element not on this page or invalid
        this.nextEditIndex = null;
      }
    }
  }

  private enterEditMode(element: HTMLElement, index: number, originalHex: string) {
    if (element.querySelector('input')) return; // already editing

    element.textContent = '';
    // element.style.backgroundColor = 'var(--accent)'; // Moved to CSS .editing
    // element.style.color = '#000'; // Moved to CSS .editing
    element.classList.add('editing');

    const input = document.createElement('input');
    input.type = 'text';
    input.value = originalHex;
    input.maxLength = 2;
    input.style.width = '100%';
    input.style.border = 'none';
    input.style.background = 'transparent';
    input.style.color = 'inherit';
    input.style.textAlign = 'center';
    input.style.fontFamily = 'inherit';
    input.style.fontSize = 'inherit';
    input.style.fontSize = 'inherit';
    input.style.outline = 'none';
    input.setAttribute('autocomplete', 'off');
    input.setAttribute('autocorrect', 'off');
    input.setAttribute('autocapitalize', 'off');
    input.setAttribute('spellcheck', 'false');
    // input.style.imeMode = 'disabled'; // TypeScript might complain, best to rely on attributes + CSS class if needed
    // But setting simple style propery is easiest if valid in CSS
    // Let's add it via style string to bypass TS check if key is missing or just ignore
    (input.style as any).imeMode = 'disabled';

    const finish = () => {
      const val = parseInt(input.value, 16);
      if (!isNaN(val) && val >= 0 && val <= 255) {
        const currentVal = this.state.current.buffer![index];
        if (currentVal !== val) {
          const newBuf = new Uint8Array(this.state.current.buffer!);
          newBuf[index] = val;
          this.state.updateBuffer(newBuf); // Synchronous render triggers here
          return;
        }
      }
      // Revert/Cancel or No Change
      element.textContent = originalHex;
      // element.style.backgroundColor = ''; // Handled by class removal
      // element.style.color = ''; // Handled by class removal
      element.classList.remove('editing');

      // Restore structure styling if needed - Handled by class .structure

      // Handle direct transition if no re-render occurred
      if (this.nextEditIndex !== null) {
        const el = this.container.querySelector(`span[data-index="${this.nextEditIndex}"]`) as HTMLElement;
        if (el) {
          const idx = this.nextEditIndex;
          this.nextEditIndex = null;
          // Recursive call immediate
          this.enterEditMode(el, idx, HexUtils.toHex(this.state.current.buffer![idx]));
        } else {
          this.nextEditIndex = null;
        }
      }
    };

    input.onblur = finish;
    input.onkeydown = (e) => {
      if (e.key === 'Enter') { input.blur(); }
      if (e.key === 'Escape') {
        input.value = originalHex;
        this.nextEditIndex = null; // Cancel navigation
        input.blur();
      }
      if (e.key === 'Tab') {
        e.preventDefault();
        const direction = e.shiftKey ? -1 : 1;
        this.nextEditIndex = index + direction;
        // Check bounds?
        if (this.nextEditIndex < 0) this.nextEditIndex = 0;
        if (this.nextEditIndex >= this.state.current.buffer!.length) this.nextEditIndex = null;

        input.blur();
      }
    };

    // Sanitization
    input.oninput = (e) => {
      const el = e.target as HTMLInputElement;
      el.value = el.value.toUpperCase().replace(/[^0-9A-F]/g, '');
    };

    element.appendChild(input);
    input.focus();
    input.select();
  }
}
